import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { ButtonComplete } from "@/components/buttons/ButtonComplete.tsx";
import { type FC, useRef, useState } from "react";
import { useStateWithDeps } from "use-state-with-deps";
import type { UnlockCallback } from "@/components/buttons/shared.ts";
import { type AssignedSession, useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { Row } from "@/components/common/row.ts";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { IconRefresh } from "@/icons/IconRefresh.tsx";
import { IconRightLine } from "@/icons/IconRightLine.tsx";
import { IconTick } from "@/icons/IconTick.tsx";
import { IconX } from "@/icons/IconX.tsx";
import type { Chat } from "@/stores/useChatStore.ts";
import { useMutation } from "@tanstack/react-query";
import { IsHeldReplyStateSomething, postChatRegenerateResponse } from "@/api/chat.ts";
import {
  defaultTeacherFlagReason,
  handbackTutorQueryKey,
  postFlowTeacherFlag,
  postFlowTutorHandover,
  teacherFlagNoteMaxLength,
  type TeacherFlagReason,
  teacherFlagReasons,
} from "@/api/admin.ts";
import { IconFlag } from "@/icons/IconFlag.tsx";
import { HttpError } from "@/api/shared.ts";
import { useInputStore } from "@/stores/useInputStore.ts";
import { ModalSimple } from "@/components/modal/ModalSimple.tsx";
import { BasicInput } from "@/components/inputs/shared.ts";
import { toast } from "sonner";
import { toastAndError } from "@/helpers/toast.ts";
import { ADT } from "pb.adt";
import { JsonParse } from "@/helpers/json.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { ExperimentArm } from "@/types/arm.ts";

const ChatFooterWrapper = styled.div`
  display: flex;
  background: ${t.chatFooterBackground};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 42px;
  flex-shrink: 0;
  padding: 0.5rem ${t.chatPaddingSides};

  &[data-ai="true"] {
    background: ${t.chatFooterAiBackground};
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const RightWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const Span = styled.span`
  color: ${t.brandTextSecondary};
`;

const AiFeedback = styled.div`
  color: white;
`;

const HandbackText = styled.p`
  padding: 1rem 0;
`;

const HandbackInput = styled(BasicInput)`
  width: 100%;
  margin-bottom: 1rem;
`;

const FlagReasonSelect = styled.select`
  width: 100%;
  margin-bottom: 1rem;
  border: ${t.brandBorderSegment};
  border-radius: ${t.brandBorderRadius};
  padding: 10px;
`;

const toastMutationError = (fallback: string) => (error: Error) => {
  const httpError = JsonParse<HttpError>(error.message);
  if (httpError) {
    toast.error(httpError.message);
    return;
  }

  toastAndError(fallback, error);
};

interface TeacherFlagModalProps {
  flowSessionId: number;
  flagOpen: boolean;
  onClose: () => void;
}

const TeacherFlagModal: FC<TeacherFlagModalProps> = ({ flowSessionId, flagOpen, onClose }) => {
  const [reason, setReason] = useState<TeacherFlagReason>(defaultTeacherFlagReason);
  const [note, setNote] = useState("");

  const flagToTeacher = useMutation({
    mutationFn: () => postFlowTeacherFlag(flowSessionId, reason, note),
    // The session push from the flag request updates the button.
    onSuccess: () => {
      toast.success("Flagged to the classroom teacher");
      onClose();
    },
    onError: toastMutationError("Could not flag to the teacher"),
  });

  return (
    <ModalSimple title={"Flag to the classroom teacher?"} onClick={onClose}>
      <HandbackText>
        The teacher sees this flag and the reason on their class dashboard, and clears it once they
        have spoken to the student. The lesson carries on.
        {flagOpen && " A flag is already open; this replaces it."}
      </HandbackText>
      <FlagReasonSelect
        aria-label={"Reason"}
        value={reason}
        onChange={(e) => setReason(e.target.value as TeacherFlagReason)}
        disabled={flagToTeacher.isPending}
      >
        {teacherFlagReasons.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </FlagReasonSelect>
      <HandbackInput
        aria-label={"Note for the teacher"}
        placeholder={"Optional note for the teacher..."}
        maxLength={teacherFlagNoteMaxLength}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={flagToTeacher.isPending}
      />
      <Row>
        <ButtonIcon
          icon={<IconX color={t.brandNeutralWhite} />}
          text={"Go back"}
          onClick={(unlock) => {
            onClose();
            unlock();
          }}
          backgroundColor={t.brandAccentRed}
          flare
        />
        <ButtonIcon
          icon={<IconTick width={18} height={18} />}
          text={"Flag"}
          onClick={(unlock) => flagToTeacher.mutate(undefined, { onSettled: unlock })}
          backgroundColor={t.brandAccentBlue}
          flare
        />
      </Row>
    </ModalSimple>
  );
};

interface FooterProps {
  chat: Chat;
  assignedSession?: AssignedSession;
  completeCallback: (unlockCallback: UnlockCallback, flowGeneratorSessionId: number) => void;
  openFeedbackCallback: () => void;
  isAiSession?: boolean;
}

export const ChatFooter: FC<FooterProps> = ({
  chat,
  assignedSession,
  completeCallback,
  openFeedbackCallback,
  isAiSession,
}) => {
  const { AiLoading } = useInputStore();
  const { SetSessionCompleted } = useAssignedChatStore();
  const { tutorDetails } = useUserStore();

  const assignedSessionId = assignedSession?.assignedChat?.flowGeneratorSessionId;

  const [clickCount, setClickCount] = useStateWithDeps(0, [assignedSessionId]);
  const [completeText, setCompleteText] = useStateWithDeps("Intervention Complete", [
    assignedSessionId,
  ]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showHandback, setShowHandback] = useStateWithDeps(false, [assignedSessionId]);
  const [tutorNotes, setTutorNotes] = useStateWithDeps("", [assignedSessionId]);
  const [showTeacherFlag, setShowTeacherFlag] = useStateWithDeps(false, [assignedSessionId]);
  const lastClickTime = useRef(0);
  const isAiLoading = AiLoading.get(chat.flowGeneratorSessionId);

  const reset = () => {
    setCompleteText("Intervention Complete");
    setClickCount(0);
  };

  const onClick = (unlockCallback: UnlockCallback) => {
    const clickCounter = clickCount + 1;

    if (clickCounter === 1) {
      setCompleteText("Are you sure you're done?");
      setTimeout(() => {
        reset();
      }, 5000);
    } else if (lastClickTime.current + 100 < performance.now()) {
      completeCallback(unlockCallback, assignedSession?.assignedChat.flowGeneratorSessionId ?? 0);
      reset();
    }

    unlockCallback();

    setClickCount(clickCounter);
    lastClickTime.current = performance.now();
  };

  const regenerateResponse = useMutation({
    mutationFn: (hint: string) =>
      postChatRegenerateResponse(chat.flowGeneratorSessionId, hint, null),
    retry: 5,
  });

  const regenerate = (hint: string, callback?: () => void) => {
    if (isAiLoading || isRegenerating) return;

    setIsRegenerating(true);
    regenerateResponse.mutateAsync(hint).finally(() => {
      setIsRegenerating(false);
      if (callback) callback();
    });
  };

  const handoverSession = useMutation({
    mutationFn: () =>
      postFlowTutorHandover(chat.flowGeneratorSessionId, tutorNotes.length > 0 ? tutorNotes : null),
    onSuccess: () => {
      SetSessionCompleted(chat.flowGeneratorSessionId);
      // Ownership changed — the hand-back probe's answer flips with it
      window.QueryClient.invalidateQueries({
        queryKey: handbackTutorQueryKey(chat.flowGeneratorSessionId),
      }).catch((e) => {
        toastAndError("Could not clear tutor cache, may need to refresh to see real data", e);
      });
      setShowHandback(false);
      setTutorNotes("");
    },
    onError: toastMutationError("Could not hand back session"),
  });

  // Kept current by the session push (useHookGlobal), so no fetch of its own.
  const teacherFlagReason = chat.flowGeneratorSession?.teacherFlagReason;

  const confirmHandback = (unlock: UnlockCallback) => {
    if (handoverSession.isPending) {
      unlock();
      return;
    }

    const toastId = toast.message(`Handing sessionn to Ai Tutor...`, {
      duration: Infinity,
      closeButton: true,
    });
    handoverSession
      .mutateAsync()
      .catch(() => {
        // Error toast handled by the mutation's onError
      })
      .finally(() => {
        toast.dismiss(toastId);
        unlock();
      });
  };

  const isAiReqIdle = ADT.match(chat.socrates.state, ["Idle"]);
  const isAiLoadingOrRegenerating =
    isAiLoading || isRegenerating || ADT.match(chat.socrates.state, ["Idle", "Fetching"]);
  const canUseTeacherReport = tutorDetails?.experimentArm === ExperimentArm.Honduras;

  return (
    <>
      <ChatFooterWrapper data-ai={isAiSession}>
        <LeftWrapper>
          {!assignedSession || assignedSession.completed ? (
            <Span>Session not claimed</Span>
          ) : (
            <>
              <AiFeedback>
                <Row data-justify={"between"}>
                  <div
                    data-tooltip={
                      isAiReqIdle
                        ? "Currently not attempting to fetch Socrates data"
                        : isAiLoadingOrRegenerating
                          ? "LLM is thinking... 🤔"
                          : "Click to attempt to regenerate an LLM response"
                    }
                    data-direction={"right"}
                  >
                    <ButtonIcon
                      icon={
                        <IconRefresh
                          bold={false}
                          color={t.brandTextSecondary}
                          rotate={!isAiReqIdle && isAiLoadingOrRegenerating}
                        />
                      }
                      text={
                        isAiReqIdle
                          ? "Idle"
                          : isAiLoadingOrRegenerating
                            ? "Fetching..."
                            : "Regenerate"
                      }
                      textColor={t.brandTextSecondary}
                      onClick={(callback) =>
                        regenerate(
                          "Regenerating response. Please attempt to adhere to brevity requirements & check for mathematical accuracy",
                          callback,
                        )
                      }
                      disabledOverride={
                        isAiReqIdle || isAiLoadingOrRegenerating || !assignedSession
                      }
                      flare
                      noPadding
                    />
                  </div>
                </Row>
              </AiFeedback>
            </>
          )}
        </LeftWrapper>
        <RightWrapper>
          {assignedSession ? (
            assignedSession.completed && !assignedSession.feedbackGiven ? (
              <>
                <ButtonComplete
                  text={"Open feedback"}
                  onClick={(unlock) => {
                    unlock();
                    openFeedbackCallback();
                  }}
                  flare
                />
              </>
            ) : assignedSession.feedbackGiven ? (
              <>
                <Span>Feedback given</Span>
              </>
            ) : (
              !IsHeldReplyStateSomething(chat.flaggedMessage.flagged) && (
                <>
                  {canUseTeacherReport && (
                    <ButtonIcon
                      icon={<IconFlag width={14} height={16} color={t.brandNeutralWhite} />}
                      // Still clickable: a new flag replaces the open one.
                      text={teacherFlagReason ? "Teacher notified" : "Flag to teacher"}
                      onClick={(unlock) => {
                        setShowTeacherFlag(true);
                        unlock();
                      }}
                      backgroundColor={t.brandAccentRed}
                      flare
                    />
                  )}
                  {
                    <ButtonIcon
                      icon={<IconRightLine width={20} height={20} color={t.brandNeutralWhite} />}
                      text={`Hand to Eedi Tutor`}
                      onClick={(unlock) => {
                        setShowHandback(true);
                        unlock();
                      }}
                      backgroundColor={t.brandAiButtonColor}
                      flare
                    />
                  }
                  <ButtonComplete text={completeText} onClick={onClick} flare />
                </>
              )
            )
          ) : null}
        </RightWrapper>
      </ChatFooterWrapper>
      {showHandback && (
        <ModalSimple
          title={`Hand back to Ai Tutor?`}
          onClick={() => {
            setShowHandback(false);
          }}
        >
          <HandbackText>
            This ends your intervention and hands the student back to EEDI's AI tutor.
            <br />
            Keep in mind this may not always work if the session was not already claimed by EEDI's
            AI tutor.
          </HandbackText>
          <HandbackInput
            placeholder={"Optional notes for the AI tutor..."}
            value={tutorNotes}
            onChange={(e) => setTutorNotes(e.target.value)}
            disabled={handoverSession.isPending}
          />
          <Row>
            <ButtonIcon
              icon={<IconX color={t.brandNeutralWhite} />}
              text={"Go back"}
              onClick={(unlock) => {
                setShowHandback(false);
                unlock();
              }}
              backgroundColor={t.brandAccentRed}
              flare
            />
            <ButtonIcon
              icon={<IconTick width={18} height={18} />}
              text={"Hand back"}
              onClick={confirmHandback}
              backgroundColor={t.brandAccentBlue}
              flare
            />
          </Row>
        </ModalSimple>
      )}
      {showTeacherFlag && (
        <TeacherFlagModal
          key={chat.flowGeneratorSessionId}
          flowSessionId={chat.flowGeneratorSessionId}
          flagOpen={!!teacherFlagReason}
          onClose={() => setShowTeacherFlag(false)}
        />
      )}
    </>
  );
};
